import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { ArrowLeft, Eraser, RotateCcw, ScanBarcode, Package, Gauge, Loader2, HardDrive, Ban, CheckCircle, X } from "lucide-react";

import { usePageTitle } from "../hooks/usePageTitle";
import { useTranslation } from "../i18n";
import { apiFetch } from "../api/apiFetch";
import HiddenScanInput from "../components/HiddenScanInput";
import PrinterSelectModal from "../components/PrinterSelectModal";
import FitWord from "../components/FitWord";
import { playSuccess, playError } from "../components/helpers/audioPlayer";
import { useReadOnly } from "../access/ReadOnly";
import { ScoreDisplay, TimeBonusPopup, useCycleScore } from "../components/gamification";
import {
    buildWipeLabel,
    buildBarcodeLabel,
    silentPrintBarcode,
    browserPrint,
    getSavedLocalPrinter,
    type WipeLabelOrientation,
} from "../components/helpers/printAgent";
import {
    submitNetworkPrintJob,
    getSavedNetworkPrinter,
    detectPrintMode,
} from "../components/helpers/networkPrint";
import "../styles/scansheet.css";
import "../styles/home.css";
import "../styles/dispatch.css";
import "../styles/wipe.css";

// "reprint" is display-only (the reprint-mode flash) — it's never recorded.
type Outcome = "auto_list" | "auto_dispose" | "data_wipe_relabel" | "relabel" | "relabel_dispose" | "cover_relabel" | "cover_unmanifested" | "box_blue" | "box_pink" | "reprint";

// Data-sensitivity suggestion for an ASIN. `dataSensitive` is the effective
// answer (human override if any, else AI); the other fields drive the hints.
interface Suggestion {
    dataSensitive: boolean;
    aiSuggestion?: boolean | null;
    humanSuggestion?: boolean | null;
    humanName?: string | null;
    // true = served from wipe_classifications (this ASIN has been seen before);
    // false = the AI has just classified it for the first time.
    cached?: boolean;
    // true = a worker has recorded a unit of this ASIN from the decide screen
    // at least once. Hands-free auto-processing applies only to confirmed
    // ASINs: the AI always predicts first, but the first unit of each ASIN
    // stops so the worker can accept or correct that prediction.
    confirmed?: boolean;
}

interface WipeItem {
    scannedBarcode: string;
    lpn: string | null;
    jliPkgId: string | null;
    asin: string | null;
    itemDesc: string | null;
    netCost: number | null;
    units: number | null;
    fcSku: string | null;
    fnSku: string | null;
    unitRecovery: number | null;
    itemPkgWeight: number | null;
    itemPkgWeightUom: string | null;
    // Manifest currency (GBP / EUR); stored on the wipe row, drives the
    // GBP conversion when the label becomes a portal product.
    currencyCode?: string | null;
}

const AUTOPRINT_KEY = "wipe.autoPrint";
// Individual items mode (station toggle, remembered per browser): the worker
// has one unit in hand. Only LPNs are accepted; an LPN with no manifest line
// of its own is resolved via the LPN weekly manifest to its box's line for
// that ASIN, and an unmatched LPN goes straight to the Unmanifested prompt.
// Plan: .claude/plans/feat-wipe-individual-items.md
const INDIVIDUAL_KEY = "wipe.individualItems";
const RELABEL_OUTCOMES: Outcome[] = ["data_wipe_relabel", "relabel", "relabel_dispose"];

// Split AI wipe instructions into clean steps, stripping any leading bullet or
// number the model may have added (the UI numbers them via an ordered list).
const parseWipeSteps = (text: string): string[] =>
    text
        .split(/\r?\n/)
        .map(l => l.replace(/^\s*(?:[-•*]|\d+[.)])\s*/, "").trim())
        .filter(Boolean);

const readAutoPrint = (): boolean => {
    try {
        const v = localStorage.getItem(AUTOPRINT_KEY);
        return v === null ? true : v === "true";
    } catch { return true; }
};

// Off by default: a fresh station runs box routing until a worker switches.
const readIndividual = (): boolean => {
    try { return localStorage.getItem(INDIVIDUAL_KEY) === "true"; } catch { return false; }
};

// Client-side mirror of the routing the BE thresholds describe. Routing is by
// SupplierUnitCost (net) against the two per-warehouse thresholds:
//   - "cheap" = at or below the dispose threshold → Yellow pallet.
//   - Data-bearing items always get a relabel; non-data ones never do, and just
//     go to the Pink (sell) or Yellow (dispose) pallet by value.
//   - Individual items mode (Alex 2026-10-27): EVERY found LPN gets a new SKU,
//     so a non-data unit is relabelled too (Pink, or Yellow when cheap) instead
//     of the label-free auto_list / auto_dispose. The data answer still picks
//     the wipe step for data devices and is stored on the row for the table;
//     every row therefore carries a SKU and reaches the Jobalots export.
export const computeOutcome = (
    dataSensitive: boolean,
    netCost: number | null,
    wipeT: number | null,
    disposeT: number | null,
    individual = false,
): Outcome => {
    const cost = typeof netCost === "number" ? netCost : 0;
    const cheap = disposeT != null && cost <= disposeT;
    if (!dataSensitive) {
        if (individual) return cheap ? "relabel_dispose" : "relabel";
        return cheap ? "auto_dispose" : "auto_list";
    }
    if (cheap) return "relabel_dispose";
    if (wipeT != null && cost >= wipeT) return "data_wipe_relabel";
    return "relabel";
};

const Wipe = () => {
    usePageTitle("wipe");
    const { t } = useTranslation();
    const navigate = useNavigate();
    // Access-control read-only handheld mode (ACCESS_CONTROL_PLAN.md §8.4).
    // HiddenScanInput already rejects scans on its own via context; the
    // buttons below are the other way a worker can trigger a mutation.
    const readOnly = useReadOnly();

    const userData = window.localStorage.getItem("user");
    const user = userData ? JSON.parse(userData) : { uid: null, name: "", warehouse: "" };

    const [phase, setPhase] = useState<"scan" | "decide">("scan");
    const [scanValue, setScanValue] = useState("");
    const [loading, setLoading] = useState(false);

    const [item, setItem] = useState<WipeItem | null>(null);
    // Total physical units behind the current single-ASIN scan (>1 ⇒ multi-unit
    // box; the decide screen offers "print all N" real, referenced barcodes).
    const [boxUnits, setBoxUnits] = useState<number>(1);
    // A data-bearing MIXED box awaiting confirmation to print N reference-free
    // cover labels — set when the scan resolves to a box that stays whole
    // (its LPNs aren't on the manifest, or it's too cheap to split) → Blue.
    const [coverPrompt, setCoverPrompt] = useState<{ count: number; jliPkgId: string | null } | null>(null);
    // A data-bearing MIXED box whose units ARE each listed by LPN and whose
    // average value is above the box threshold: the worker should scan each
    // LPN so every unit gets its own real relabel. "Cover instead" is offered
    // for a damaged box (same cover flow as above).
    const [scanLpnsPrompt, setScanLpnsPrompt] = useState<{ count: number; avgCost: number | null; jliPkgId: string | null } | null>(null);
    // Mirror for doScan (a stable callback): while the "scan the first LPN"
    // prompt is up the scanner stays live — an LPN closes it and runs as normal,
    // anything else is refused so the worker can't skip past it by accident.
    const scanLpnsOpenRef = useRef(false);
    useEffect(() => { scanLpnsOpenRef.current = !!scanLpnsPrompt; }, [scanLpnsPrompt]);
    // An LPN that is on no manifest at all: offer one cover label and send the
    // unit to the Unmanifested pallet (black).
    const [unmanifestedPrompt, setUnmanifestedPrompt] = useState<{ lpn: string } | null>(null);
    const [thresholds, setThresholds] = useState<{ wipe: number | null; dispose: number | null }>({ wipe: null, dispose: null });
    const [dataSensitive, setDataSensitive] = useState<boolean>(false);
    const [aiSuggestion, setAiSuggestion] = useState<boolean | null>(null);
    // A prior human override of the AI answer (null = never manually changed).
    const [humanSuggestion, setHumanSuggestion] = useState<boolean | null>(null);
    const [humanName, setHumanName] = useState<string | null>(null);
    const [instructions, setInstructions] = useState<string>("");
    const [instructionsLoading, setInstructionsLoading] = useState(false);

    // Label dimensions (per-warehouse) from settings.
    const [labelDims, setLabelDims] = useState<{ w: number; h: number; orientation: WipeLabelOrientation }>({ w: 65, h: 38, orientation: "ccw" });

    // Printer + auto-print preference.
    const [printMode, setPrintMode] = useState<"local" | "network" | null>(null);
    const [printerOnline, setPrinterOnline] = useState(false);
    const [showPrinterSelect, setShowPrinterSelect] = useState(false);
    const [autoPrint, setAutoPrint] = useState<boolean>(readAutoPrint());
    const [individual, setIndividual] = useState<boolean>(readIndividual());
    // Reprint mode — while on, a scanned LPN (or PkgID) reprints the original
    // sp… PkgID barcode instead of going through the wipe flow. Deliberately
    // not persisted: it must never be accidentally left on across sessions.
    const [reprintMode, setReprintMode] = useState(false);

    // Stats.
    const [stats, setStats] = useState<{ itemsToday: number; itemsPerHour: number }>({
        itemsToday: 0, itemsPerHour: 0,
    });

    // "Go back" banner — the just-recorded item, kept around so the worker can
    // re-open it to correct a misclassification.
    const [lastRecord, setLastRecord] = useState<{
        ids: number[];
        relabelBarcodes: string[];
        item: WipeItem;
        // What was actually used for this record, so "Go back" restores it
        // instead of defaulting to Yes.
        dataSensitive: boolean;
        aiSuggestion: boolean | null;
        humanSuggestion: boolean | null;
        humanName: string | null;
    } | null>(null);
    const undoTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Transient "what just happened" message shown in the scan centre for a few
    // seconds after a hands-free auto-process, so the worker knows the action.
    const [autoAction, setAutoAction] = useState<{ outcome: Outcome; barcode: string | null; title?: string | null } | null>(null);
    const autoActionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Once the worker manually picks data-sensitive yes/no, the async AI
    // suggestion must not override their choice. Reset on each new scan.
    const sensitivityTouchedRef = useRef(false);
    // Mirror of `item` for the async classify callback's stale-guard.
    const itemRef = useRef<WipeItem | null>(null);
    useEffect(() => { itemRef.current = item; }, [item]);
    // Per-warehouse "auto-process non-data items" setting, read as a ref so the
    // async classify callback always sees the latest value.
    const autoProcessNoRef = useRef(false);
    // Latest confirm() for the auto-process path to call.
    const confirmRef = useRef<(opts?: { quantity?: number }) => void>(() => {});

    const outcome = item ? computeOutcome(dataSensitive, item.netCost, thresholds.wipe, thresholds.dispose, individual) : "auto_list";

    // A multi-unit box scanned by its carton (not an individual LPN): the only
    // print action is "Print all N" — never a single label for an unidentified
    // unit. An LPN scan (item.lpn set) or a single item keeps the normal Confirm.
    const isBoxCarton = boxUnits > 1 && !item?.lpn;

    // ---- Settings (label size + threshold fallback) on mount ----
    useEffect(() => {
        if (!user.warehouse) return;
        let alive = true;
        (async () => {
            try {
                const res = await apiFetch(`/settings/${encodeURIComponent(user.warehouse)}`);
                if (!res.ok) return;
                const s = await res.json();
                if (!alive) return;
                setLabelDims({
                    w: Number(s.wipe_label_width_mm) || 65,
                    h: Number(s.wipe_label_height_mm) || 38,
                    orientation: (s.wipe_label_orientation as WipeLabelOrientation) || "ccw",
                });
                autoProcessNoRef.current = Boolean(s.wipe_auto_process_no);
            } catch { /* defaults apply */ }
        })();
        return () => { alive = false; };
    }, [user.warehouse]);

    // ---- Printer detection (shared, preference-aware — an explicit "Local"
    // choice in the printer modal holds even while a previously-saved
    // network printer is online) ----
    const detectPrinter = useCallback(async () => {
        const mode = await detectPrintMode();
        setPrintMode(mode);
        setPrinterOnline(mode !== null);
    }, []);
    useEffect(() => { detectPrinter(); }, [detectPrinter]);

    // ---- Stats polling ----
    const refreshStats = useCallback(async () => {
        if (!user.uid) return;
        try {
            const res = await apiFetch(`/wipe/stats?warehouse=${encodeURIComponent(user.warehouse || "")}&uid=${encodeURIComponent(user.uid)}`);
            if (!res.ok) return;
            const data = await res.json();
            setStats(data);
        } catch { /* transient */ }
    }, [user.uid, user.warehouse]);
    useEffect(() => {
        refreshStats();
        const id = setInterval(refreshStats, 15000);
        return () => clearInterval(id);
    }, [refreshStats]);

    // Trophy points. The unit of work is one recorded item, so the window
    // runs record-to-record — grading and the walk to the next unit are part
    // of the job being measured. No progress bar here: a bench where an
    // operator legitimately stops to inspect a device doesn't need a clock
    // ticking down at them.
    const cycle = useCycleScore(user.warehouse, "wipe_timer_ms", "wipe-best-time");
    const cycleRef = useRef(cycle);
    cycleRef.current = cycle;

    // ---- Record a whole box (multi-product package, no data-bearing items) ----
    // The box goes straight to a pallet (Blue, or Pink above the box threshold);
    // no decide screen, no label. Shows the action in the scan centre like the
    // hands-free auto-process path.
    const recordBox = useCallback(async (box: {
        outcome: Outcome; jliPkgId: string | null; itemDesc: string | null;
        avgCost: number | null; itemCount: number;
    }, scannedBarcode: string) => {
        try {
            const res = await apiFetch(`/wipe/record`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    scannedBarcode,
                    jliPkgId: box.jliPkgId,
                    itemDesc: box.itemDesc,
                    supplierUnitCost: box.avgCost,
                    quantity: box.itemCount,
                    dataSensitive: false,
                    outcome: box.outcome,
                    warehouse: user.warehouse,
                    uid: user.uid,
                    name: user.name,
                }),
            });
            if (res.status === 401) { navigate("/login"); return; }
            if (!res.ok) { playError(); toast.error(t("wipe_print_failed"), { toastId: "wipe-box-err" }); return; }
            playSuccess();
            setAutoAction({ outcome: box.outcome, barcode: `${box.itemCount} ${t("wipe_items")}` });
            if (autoActionTimerRef.current) clearTimeout(autoActionTimerRef.current);
            autoActionTimerRef.current = setTimeout(() => setAutoAction(null), 5000);
            refreshStats();
            cycleRef.current.complete();
        } catch {
            playError();
            toast.error(t("wipe_print_failed"), { toastId: "wipe-box-catch" });
        }
    }, [user.warehouse, user.uid, user.name, navigate, t, refreshStats]);

    // ---- Reprint mode: scan an LPN or PkgID → reprint the original PkgID barcode ----
    // Reuses the scansheet's /scan/check resolver (a PkgID echoes itself; an
    // LPN resolves to its box's PkgID) and prints the same plain barcode label
    // the scansheet prints. Nothing is recorded in wipe_products.
    const doReprint = useCallback(async (barcode: string) => {
        setLoading(true);
        try {
            const res = await apiFetch(`/scan/check`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ scannedItem: barcode }),
            });
            if (res.status === 401) { navigate("/login"); return; }
            const data = await res.json().catch(() => null);
            const pkgId: string | null = data?.response?.PkgID ?? null;
            if (!res.ok || !pkgId) {
                playError();
                toast.error(t("wipe_not_found"), { toastId: "wipe-reprint-nf" });
                return;
            }
            const saved = getSavedNetworkPrinter();
            const dims = { labelWidthMm: labelDims.w, labelHeightMm: labelDims.h };
            let ok = false;
            if (saved && printMode === "network") {
                ok = (await submitNetworkPrintJob(saved.agentId, saved.printerName, buildBarcodeLabel(pkgId), 1, pkgId, false, dims)).success;
            }
            if (!ok) ok = (await silentPrintBarcode(pkgId, 1, { printer: getSavedLocalPrinter() ?? undefined, ...dims })).success;
            if (!ok) {
                playError();
                toast.error(t("wipe_print_failed"), { toastId: "wipe-reprint-fail" });
                return;
            }
            playSuccess();
            apiFetch(`/admin/logs/event`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    department: "Wipe",
                    action: "wipe.label_print",
                    metadata: { pkgid: pkgId, scanned: barcode, copies: 1, success: true, reprint: true },
                }),
            }).catch(() => {});
            // Brief confirmation flash showing what was printed; scanning stays
            // live, so the next scan can cut it short.
            setAutoAction({ outcome: "reprint", barcode: pkgId });
            if (autoActionTimerRef.current) clearTimeout(autoActionTimerRef.current);
            autoActionTimerRef.current = setTimeout(() => setAutoAction(null), 2000);
        } catch {
            playError();
            toast.error(t("wipe_print_failed"), { toastId: "wipe-reprint-catch" });
        } finally {
            setScanValue("");
            setLoading(false);
        }
    }, [navigate, t, printMode, labelDims]);

    // ---- Scan ----
    const doScan = useCallback(async (raw?: string) => {
        const barcode = (raw ?? scanValue).trim();
        if (!barcode || loading) return;
        if (reprintMode) { await doReprint(barcode); return; }
        // "Scan the first LPN" prompt is up: only an LPN may close it. Anything
        // else (the box barcode again, a product barcode) is refused.
        if (scanLpnsOpenRef.current) {
            if (!/^LPN/i.test(barcode)) {
                playError();
                toast.error(t("wipe_scan_lpns_not_lpn"), { toastId: "wipe-scan-lpns-not-lpn", autoClose: 3000 });
                setScanValue("");
                return;
            }
            setScanLpnsPrompt(null);
        }
        setLoading(true);
        try {
            const res = await apiFetch(`/wipe/scan`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ barcode, warehouse: user.warehouse, uid: user.uid, name: user.name, individual }),
            });
            if (res.status === 401) { navigate("/login"); return; }
            const data = await res.json();
            if (!res.ok || !data.found) {
                playError();
                setScanValue("");
                // Individual items mode refuses anything that is not an LPN
                // (a box or repack code): nothing is recorded.
                if (res.ok && data.notLpn) {
                    toast.error(t("wipe_individual_not_lpn"), { toastId: "wipe-individual-not-lpn", autoClose: 3000 });
                    return;
                }
                // An LPN on no manifest at all (or, in Individual mode, with no
                // LPN Full match): offer a cover label for the Unmanifested
                // pallet. Anything else is a plain not-found.
                if (res.ok && data.isLpn) {
                    setAutoAction(null);
                    setUnmanifestedPrompt({ lpn: barcode });
                    return;
                }
                toast.error(t("wipe_not_found"), { toastId: "wipe-not-found", autoClose: 3500 });
                return;
            }
            // Data-bearing MIXED box whose units are each listed by LPN and worth
            // splitting: tell the worker to scan each LPN (each then gets its own
            // real relabel by the single-item rules). "Cover instead" is offered.
            if (data.scanLpns) {
                setScanValue("");
                setAutoAction(null);
                playError(); // needs a decision — not a straight-through success
                setScanLpnsPrompt({
                    count: Number(data.count) || 0,
                    avgCost: typeof data.avgCost === "number" ? data.avgCost : null,
                    jliPkgId: data.jliPkgId ?? null,
                });
                return;
            }
            // Data-bearing MIXED box that stays whole (LPNs not on the manifest,
            // or too cheap to split): ask the worker to confirm printing one
            // reference-free cover label per unit (covering each old barcode
            // before data-wiping), then Blue.
            if (data.coverLabels) {
                setScanValue("");
                setAutoAction(null); // don't let a previous flash bleed behind the modal
                playError(); // needs a decision — not a straight-through success
                setCoverPrompt({ count: Number(data.count) || 0, jliPkgId: data.jliPkgId ?? null });
                return;
            }
            // Whole box (multi-product package, no data-bearing items) — record it
            // straight to its pallet, no decide screen.
            if (data.box) {
                setScanValue("");
                await recordBox(data, barcode);
                return;
            }
            setAutoAction(null); // a new scan clears the previous action message
            sensitivityTouchedRef.current = false;
            setInstructions("");
            setScanValue("");
            setBoxUnits(Number(data.boxUnits) || 1); // >1 ⇒ offer "print all N"
            setThresholds(data.thresholds || { wipe: null, dispose: null });

            const desc = data.item?.itemDesc;
            const asin = data.item?.asin;
            const scannedFor = data.item?.scannedBarcode;

            // A "box carton" scan = a multi-unit box scanned by its carton/product
            // barcode, NOT by an individual LPN. We must never auto-print for these:
            // the worker hasn't identified a single unit, so a label would print
            // with no idea which item it belongs to. They either tap "Print all N"
            // explicitly, or scan an LPN to relabel one unit. A single unit or an
            // LPN scan (item.lpn set) is fine to auto-process as before.
            const isBoxCarton = (Number(data.boxUnits) || 1) > 1 && !data.item?.lpn;

            // Do we already know (from the DB cache) the data-sensitivity, and
            // does it route to a hands-free skip-outcome (anything but a wipe)?
            // If so, skip the decide screen entirely — recording straight from
            // the scan screen avoids the brief flicker of the 2nd page.
            // The suggestion carries the EFFECTIVE answer (human override if any,
            // else AI), plus the AI answer and any human override separately.
            //
            // Hands-free applies only to an ASIN a worker has CONFIRMED before
            // (suggestion.confirmed). The AI always predicts first, but the
            // first unit of each ASIN stops on the decide screen with that
            // prediction pre-selected, so a wrong AI call can be corrected
            // before anything prints; the worker's confirm (changed or not) is
            // what later scans run hands-free on. This also covers units of a
            // split mixed box: the box scan classifies its ASINs, but nobody
            // has confirmed them yet, so each new ASIN's first LPN stops.
            const sug = data.suggestion as Suggestion | null;
            const cachedDS = sug && typeof sug.dataSensitive === "boolean" ? sug.dataSensitive : null;
            const cachedOutcome = cachedDS === null ? null
                : computeOutcome(cachedDS, data.item?.netCost ?? null, data.thresholds?.wipe ?? null, data.thresholds?.dispose ?? null, individual);
            // A multi-unit box scanned by its carton is never auto-PRINTED (which
            // unit gets which label is the worker's call — they tap "Print all N"),
            // but a confirmed NON-data box has nothing to print: record it as one
            // Pink/Yellow row carrying the unit count, hands-free.
            const units = Number(data.boxUnits) || 1;
            const nonDataOutcome = cachedOutcome === "auto_list" || cachedOutcome === "auto_dispose";
            const autoSkip = (!isBoxCarton || nonDataOutcome) && cachedDS !== null && sug?.confirmed === true
                && autoProcessNoRef.current && cachedOutcome !== "data_wipe_relabel";

            setItem(data.item);

            // Pre-fill the AI / human suggestion hints + select the Yes/No card
            // from the effective answer (unless the worker has already chosen).
            const applySuggestion = (s: Suggestion) => {
                setAiSuggestion(typeof s.aiSuggestion === "boolean" ? s.aiSuggestion : s.dataSensitive);
                setHumanSuggestion(typeof s.humanSuggestion === "boolean" ? s.humanSuggestion : null);
                setHumanName(s.humanName ?? null);
                if (!sensitivityTouchedRef.current) setDataSensitive(s.dataSensitive);
                const oc = computeOutcome(
                    s.dataSensitive, data.item?.netCost ?? null,
                    data.thresholds?.wipe ?? null, data.thresholds?.dispose ?? null,
                    individual,
                );
                // Auto-confirm only an answer a worker has already confirmed —
                // an AI-only classification stays on the decide screen. A box
                // carton auto-confirms only when there is nothing to print.
                const ocNonData = oc === "auto_list" || oc === "auto_dispose";
                if ((!isBoxCarton || ocNonData) && autoProcessNoRef.current && s.confirmed === true
                    && !sensitivityTouchedRef.current && oc !== "data_wipe_relabel") {
                    setTimeout(() => {
                        if (!sensitivityTouchedRef.current && itemRef.current?.scannedBarcode === scannedFor) {
                            confirmRef.current({ quantity: isBoxCarton ? units : 1 });
                        }
                    }, 0);
                }
            };

            if (autoSkip && sug) {
                setDataSensitive(cachedDS as boolean);
                setAiSuggestion(typeof sug.aiSuggestion === "boolean" ? sug.aiSuggestion : cachedDS);
                setHumanSuggestion(typeof sug.humanSuggestion === "boolean" ? sug.humanSuggestion : null);
                setHumanName(sug.humanName ?? null);
                // No beep here — the 2nd page is skipped, and confirm() beeps once
                // when it shows the wipe-auto-result, so this would be a double beep.
                // Stay on the scan screen (no decide flicker); record next tick
                // once the item/dataSensitive state has flushed.
                setTimeout(() => {
                    if (!sensitivityTouchedRef.current && itemRef.current?.scannedBarcode === scannedFor) {
                        confirmRef.current({ quantity: isBoxCarton ? units : 1 });
                    }
                }, 0);
                return;
            }

            // Otherwise show the decide screen.
            setAiSuggestion(null);
            setHumanSuggestion(null);
            setHumanName(null);
            setDataSensitive(false);
            setPhase("decide");
            playSuccess();

            if (sug) {
                // Known ASIN, but needs the screen (auto-process off, or it's a
                // wipe) — just pre-fill the suggestion.
                applySuggestion(sug);
            } else if (desc) {
                // Never-seen ASIN — classify asynchronously (the endpoint caches
                // the result per ASIN so next time it comes back with the scan).
                apiFetch(`/wipe/classify?asin=${encodeURIComponent(asin || "")}&desc=${encodeURIComponent(desc)}`)
                    .then(r => (r.ok ? r.json() : null))
                    .then((s: Suggestion | null) => {
                        if (!s || typeof s.dataSensitive !== "boolean") return;
                        if (itemRef.current?.scannedBarcode !== scannedFor) return;
                        applySuggestion(s);
                    })
                    .catch(() => {});
            }
        } catch {
            playError();
            toast.error(t("wipe_not_found"), { toastId: "wipe-scan-err" });
        } finally {
            setLoading(false);
        }
    }, [scanValue, loading, reprintMode, individual, doReprint, user.warehouse, user.uid, user.name, navigate, t, recordBox]);

    // ---- Fetch wipe instructions when the outcome needs them ----
    useEffect(() => {
        if (phase !== "decide" || outcome !== "data_wipe_relabel" || !item?.asin) {
            return;
        }
        let alive = true;
        setInstructions("");
        setInstructionsLoading(true);
        (async () => {
            try {
                const res = await apiFetch(
                    `/wipe/instructions/${encodeURIComponent(item.asin!)}?desc=${encodeURIComponent(item.itemDesc || "")}`,
                );
                if (!res.ok) return;
                const data = await res.json();
                if (alive) setInstructions(data.instructions || "");
            } catch { /* fallback handled server-side */ }
            finally { if (alive) setInstructionsLoading(false); }
        })();
        return () => { alive = false; };
    }, [phase, outcome, item?.asin, item?.itemDesc]);

    // ---- Print a relabel label (network → local agent → browser) ----
    const printLabel = useCallback(async (barcode: string, dispose: boolean, copies: number) => {
        const html = buildWipeLabel(barcode, {
            widthMm: labelDims.w, heightMm: labelDims.h, dispose, orientation: labelDims.orientation,
        });
        const saved = getSavedNetworkPrinter();
        // Per-warehouse label stock size travels with BOTH ZPL paths (agent
        // ≥ 1.5.0) so short stock (e.g. 70×30) prints on one label instead
        // of the 65×38 default spilling onto two.
        const dims = { labelWidthMm: labelDims.w, labelHeightMm: labelDims.h };
        if (saved && printMode === "network") {
            const r = await submitNetworkPrintJob(saved.agentId, saved.printerName, html, copies, barcode, dispose, dims);
            if (r.success) return true;
        }
        // Zebra targets print native ZPL (same output as the network path — the
        // per-warehouse HTML orientation doesn't apply); non-Zebra targets fall
        // back to the wipe HTML label inside silentPrintBarcode.
        const local = await silentPrintBarcode(barcode, copies, { dispose, fallbackHtml: html, printer: getSavedLocalPrinter() ?? undefined, ...dims });
        if (local.success) return true;
        const browser = await browserPrint(html);
        return browser.success;
    }, [labelDims, item?.itemDesc, user.warehouse, printMode]);

    // ---- Print N reference-free cover labels for a data-bearing mixed box ----
    // One unique barcode + one wipe_products row per unit (outcome cover_relabel,
    // no ASIN). The barcodes cover each old barcode and resolve to nothing when
    // scanned — the wipe lookup never matches this generated format.
    const recordCoverLabels = useCallback(async (info: { count: number; jliPkgId: string | null }) => {
        if (!info.count) { setCoverPrompt(null); return; }
        setLoading(true);
        try {
            const res = await apiFetch(`/wipe/record`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    jliPkgId: info.jliPkgId,
                    quantity: info.count,
                    dataSensitive: true,
                    outcome: "cover_relabel",
                    warehouse: user.warehouse,
                    uid: user.uid,
                    name: user.name,
                }),
            });
            if (res.status === 401) { navigate("/login"); return; }
            if (!res.ok) { playError(); toast.error(t("wipe_print_failed"), { toastId: "wipe-cover-err" }); return; }
            const data = await res.json();
            const barcodes: string[] = Array.isArray(data.relabel_barcodes) ? data.relabel_barcodes : [];
            let allOk = true;
            for (const bc of barcodes) {
                const ok = await printLabel(bc, false, 1);
                allOk = allOk && ok;
                apiFetch(`/admin/logs/event`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        department: "Wipe",
                        action: "wipe.label_print",
                        metadata: { relabel_barcode: bc, copies: 1, success: ok, cover: true },
                    }),
                }).catch(() => {});
            }
            if (!allOk) toast.warn(t("wipe_print_failed"), { toastId: "wipe-cover-print-fail" });
            playSuccess();
            // The covered box goes on the Blue pallet — flash the scansheet-style
            // full-page BLUE cue, then return to the scan prompt. Scanning stays
            // live during the flash, so a quick worker can skip straight past it.
            setAutoAction({ outcome: "box_blue", barcode: `${barcodes.length} ${t("wipe_labels")}` });
            if (autoActionTimerRef.current) clearTimeout(autoActionTimerRef.current);
            autoActionTimerRef.current = setTimeout(() => setAutoAction(null), 2000);
            refreshStats();
            cycleRef.current.complete();
        } catch {
            playError();
            toast.error(t("wipe_print_failed"), { toastId: "wipe-cover-catch" });
        } finally {
            setCoverPrompt(null);
            setScanLpnsPrompt(null);
            setLoading(false);
        }
    }, [user.warehouse, user.uid, user.name, navigate, t, printLabel, refreshStats]);

    // ---- Print ONE cover label for an LPN that is on no manifest ----
    // Recorded as cover_unmanifested (no ASIN / cost — just the scanned LPN),
    // printed, then a full-page BLACK flash: the unit goes on the Unmanifested
    // pallet. These rows show in Admin › Wipe but never reach the exports.
    const recordUnmanifested = useCallback(async (info: { lpn: string }) => {
        setLoading(true);
        try {
            const res = await apiFetch(`/wipe/record`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    lpn: info.lpn,
                    scannedBarcode: info.lpn,
                    quantity: 1,
                    dataSensitive: true,
                    outcome: "cover_unmanifested",
                    warehouse: user.warehouse,
                    uid: user.uid,
                    name: user.name,
                }),
            });
            if (res.status === 401) { navigate("/login"); return; }
            if (!res.ok) { playError(); toast.error(t("wipe_print_failed"), { toastId: "wipe-unmanifested-err" }); return; }
            const data = await res.json();
            const barcode: string | undefined = Array.isArray(data.relabel_barcodes) ? data.relabel_barcodes[0] : undefined;
            if (!barcode) { playError(); toast.error(t("wipe_print_failed"), { toastId: "wipe-unmanifested-err" }); return; }
            const ok = await printLabel(barcode, false, 1);
            apiFetch(`/admin/logs/event`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    department: "Wipe",
                    action: "wipe.label_print",
                    metadata: { relabel_barcode: barcode, copies: 1, success: ok, cover: true, unmanifested: true, lpn: info.lpn },
                }),
            }).catch(() => {});
            if (!ok) toast.warn(t("wipe_print_failed"), { toastId: "wipe-unmanifested-print-fail" });
            playSuccess();
            setAutoAction({ outcome: "cover_unmanifested", barcode, title: info.lpn });
            if (autoActionTimerRef.current) clearTimeout(autoActionTimerRef.current);
            autoActionTimerRef.current = setTimeout(() => setAutoAction(null), 2000);
            refreshStats();
            cycleRef.current.complete();
        } catch {
            playError();
            toast.error(t("wipe_print_failed"), { toastId: "wipe-unmanifested-catch" });
        } finally {
            setUnmanifestedPrompt(null);
            setLoading(false);
        }
    }, [user.warehouse, user.uid, user.name, navigate, t, printLabel, refreshStats]);

    // ---- Confirm / record ----
    // `auto` is set by the hands-free auto-process path; it forces the label to
    // print regardless of the per-device auto-print toggle.
    const confirm = useCallback(async (opts?: { auto?: boolean; quantity?: number }) => {
        if (!item || loading) return;
        setLoading(true);
        try {
            // "Print all N" (multi-unit single-ASIN box) records one referenced
            // barcode per unit; the default confirm records the one scanned unit.
            const quantity = Math.max(1, Math.round(opts?.quantity ?? 1));
            const res = await apiFetch(`/wipe/record`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    scannedBarcode: item.scannedBarcode,
                    lpn: item.lpn,
                    jliPkgId: item.jliPkgId,
                    asin: item.asin,
                    itemDesc: item.itemDesc,
                    supplierUnitCost: item.netCost,
                    units: item.units,
                    fcSku: item.fcSku,
                    fnSku: item.fnSku,
                    unitRecovery: item.unitRecovery,
                    itemPkgWeight: item.itemPkgWeight,
                    itemPkgWeightUom: item.itemPkgWeightUom,
                    currencyCode: item.currencyCode ?? null,
                    dataSensitive,
                    humanChanged: sensitivityTouchedRef.current,
                    outcome,
                    quantity,
                    individual,
                    warehouse: user.warehouse,
                    uid: user.uid,
                    name: user.name,
                }),
            });
            if (res.status === 401) { navigate("/login"); return; }
            if (!res.ok) { playError(); toast.error(t("wipe_print_failed"), { toastId: "wipe-record-err" }); return; }
            const data = await res.json();
            // Each unit gets its own unique barcode (relabel outcomes); auto_list
            // returns none. Fall back to legacy single-barcode shape defensively.
            const relabelBarcodes: string[] = Array.isArray(data.relabel_barcodes)
                ? data.relabel_barcodes
                : (data.relabel_barcode ? [data.relabel_barcode] : []);
            const ids: number[] = Array.isArray(data.ids)
                ? data.ids
                : (data.id != null ? [data.id] : []);

            // Print (relabel outcomes only) — one label per unique barcode. Auto-
            // print toggle governs the manual path; the hands-free auto-process
            // path always prints.
            if (relabelBarcodes.length && RELABEL_OUTCOMES.includes(outcome) && (autoPrint || opts?.auto)) {
                let allOk = true;
                for (const bc of relabelBarcodes) {
                    // Every data-bearing outcome just gets a plain relabel; the
                    // dispose outcome now routes to the yellow pallet rather than
                    // being disposed, so no DISPOSE banner is printed.
                    const ok = await printLabel(bc, false, 1);
                    allOk = allOk && ok;
                    // Fire-and-forget audit of each print action.
                    apiFetch(`/admin/logs/event`, {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({
                            department: "Wipe",
                            action: "wipe.label_print",
                            metadata: { relabel_barcode: bc, copies: 1, success: ok },
                        }),
                    }).catch(() => {});
                }
                if (!allOk) toast.warn(t("wipe_print_failed"), { toastId: "wipe-print-fail" });
            }

            playSuccess();
            setLastRecord({ ids, relabelBarcodes, item, dataSensitive, aiSuggestion, humanSuggestion, humanName });
            if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
            undoTimerRef.current = setTimeout(() => setLastRecord(null), 30000);

            // Hands-free path: surface the action in the scan centre for 3s so
            // the worker sees what happened without it slowing them down. Include
            // the product title — on the auto-process path (a previously-classified
            // ASIN) the decide screen is skipped, so this is the only place the
            // worker sees WHAT they just scanned.
            if (opts?.auto) {
                setAutoAction({ outcome, barcode: relabelBarcodes[0] ?? null, title: item.itemDesc || item.asin || null });
                if (autoActionTimerRef.current) clearTimeout(autoActionTimerRef.current);
                autoActionTimerRef.current = setTimeout(() => setAutoAction(null), 5000);
            }

            // Back to scanning straight away — keep it snappy.
            setItem(null);
            setPhase("scan");
            setScanValue("");
            refreshStats();
            cycleRef.current.complete();
        } catch {
            playError();
            toast.error(t("wipe_print_failed"), { toastId: "wipe-record-catch" });
        } finally {
            setLoading(false);
        }
    }, [item, loading, dataSensitive, outcome, autoPrint, individual, printLabel, user.warehouse, user.uid, user.name, navigate, t, refreshStats]);

    // Keep confirmRef pointing at the latest confirm() so the hands-free
    // auto-process path calls the current version (forcing the label to print).
    useEffect(() => { confirmRef.current = (opts) => { confirm({ auto: true, quantity: opts?.quantity }); }; }, [confirm]);

    // ---- Go back — re-open the just-recorded item to correct it ----
    // Undoes the original record (so it isn't double-counted) and restores the
    // decide screen for that item exactly as it was: the Yes/No card defaults to
    // what was last used (the human override if one was made, else the AI answer),
    // and the same suggestion hints are shown.
    const goBackToRecord = useCallback(async () => {
        if (!lastRecord) return;
        const { ids, item: recordedItem } = lastRecord;
        // Undo every row created for this record (one per unit).
        await Promise.all(
            ids.map((id) => apiFetch(`/wipe/undo/${id}`, { method: "POST" }).catch(() => { /* ignore */ })),
        );
        if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
        // Clear any in-flight auto-result flash — otherwise its full-body colour
        // (and white text) would bleed into the decide screen we're opening.
        setAutoAction(null);
        if (autoActionTimerRef.current) clearTimeout(autoActionTimerRef.current);
        sensitivityTouchedRef.current = true; // don't let the AI re-override
        setItem(recordedItem);
        setDataSensitive(lastRecord.dataSensitive); // default to what was last used
        setAiSuggestion(lastRecord.aiSuggestion);
        setHumanSuggestion(lastRecord.humanSuggestion);
        setHumanName(lastRecord.humanName);
        setInstructions("");
        setPhase("decide");
        setLastRecord(null);
        refreshStats();
    }, [lastRecord, refreshStats]);

    // ---- Enter confirms during the decide phase ----
    useEffect(() => {
        if (phase !== "decide") return;
        const onKey = (e: KeyboardEvent) => {
            const tag = (e.target as HTMLElement)?.tagName;
            if (tag === "INPUT" || tag === "TEXTAREA") return;
            if (e.key === "Enter") {
                e.preventDefault();
                // Non-data box carton: Enter mirrors the Close and Return button
                // — nothing to print or record, just back to the scan prompt.
                if (isBoxCarton && !dataSensitive) {
                    setItem(null);
                    setPhase("scan");
                    setScanValue("");
                    return;
                }
                confirm(isBoxCarton ? { quantity: boxUnits } : undefined);
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [phase, confirm, isBoxCarton, boxUnits, dataSensitive]);

    const toggleAutoPrint = (next: boolean) => {
        setAutoPrint(next);
        try { localStorage.setItem(AUTOPRINT_KEY, String(next)); } catch { /* ignore */ }
    };

    // Individual items mode — remembered per browser so a station keeps its
    // mode across reloads and logins.
    const toggleIndividual = () => {
        setIndividual(prev => {
            const next = !prev;
            try { localStorage.setItem(INDIVIDUAL_KEY, String(next)); } catch { /* ignore */ }
            return next;
        });
    };

    // Back: from the decide page return to the scan prompt; from the scan
    // page leave the module.
    const goBack = () => {
        if (phase === "decide") {
            setItem(null);
            setPhase("scan");
            setScanValue("");
        } else {
            navigate("/");
        }
    };

    // Worker manually picks data-sensitive — lock out the async AI override.
    const pickSensitivity = (val: boolean) => {
        sensitivityTouchedRef.current = true;
        setDataSensitive(val);
    };

    // Throughput pill — rendered inside the scan bar's toggle row (extraToggle)
    // so it lifts with the bar exactly like the printer/keyboard icons do.
    // Individual items mode switch — sits in the scan bar next to the stats
    // pill. Green while on; the scan prompt also changes to "Scan LPN".
    const modePill = (
        <button
            type="button"
            className={`wipe-mode-pill ${individual ? "active" : ""}`}
            onClick={toggleIndividual}
            disabled={loading || phase === "decide" || !!coverPrompt || !!scanLpnsPrompt || !!unmanifestedPrompt}
            aria-pressed={individual}
            title={t("wipe_individual_items_hint")}
        >
            <ScanBarcode size={16} />
            <span>{t("wipe_individual_items")}</span>
            <span className={`wipe-mode-pill-state ${individual ? "on" : ""}`}>{individual ? t("wipe_mode_on") : t("wipe_mode_off")}</span>
        </button>
    );

    const statsPill = (
        <div className="wipe-stats-pill">
            <p className="dispatch-stats-title">{t("session_stats")}</p>
            <div className="dispatch-stats-row">
                <span className="dispatch-stats-item"><Package size={14} />{stats.itemsToday} {t("wipe_today")}</span>
                <span className="dispatch-stats-divider" />
                <span className="dispatch-stats-item"><Gauge size={14} />{stats.itemsPerHour} {t("wipe_per_hour")}</span>
                {cycle.scoring && (
                    <>
                        <span className="dispatch-stats-divider" />
                        <ScoreDisplay total={cycle.total} />
                    </>
                )}
            </div>
            {cycle.scoring && (
                <TimeBonusPopup
                    seconds={cycle.bonus.seconds}
                    color={cycle.bonus.color}
                    streak={cycle.bonus.streak}
                    isBest={cycle.bonus.isBest}
                    bumpKey={cycle.bonus.key}
                />
            )}
        </div>
    );

    return (
        <div className={`scan-panel wipe-page${phase === "decide" ? " wipe-page--decide" : ""}${autoAction ? ` wipe-page--flash wipe-page--${autoAction.outcome}` : ""}`}>
            <div className="page-title-bar">
                <h1 className="page-title"><Eraser size={22} strokeWidth={1.6} style={{ verticalAlign: "-4px", marginRight: 6 }} />{t("wipe")}</h1>
                <button className="page-back-btn" onClick={goBack} aria-label="Back"><ArrowLeft size={20} /></button>
            </div>

            {phase === "scan" && (
                <div className="scan-centre">
                    {autoAction ? (
                        <div className={`wipe-auto-result wipe-auto-result--${autoAction.outcome}`}>
                            <CheckCircle className="wipe-auto-result-check" size={120} strokeWidth={3} />
                            {autoAction.title && <div className="wipe-auto-result-title">{autoAction.title}</div>}
                            <div className="wipe-auto-result-action">
                                {t(`wipe_outcome_${autoAction.outcome}`).split(" ").filter(Boolean).map((word, i) => (
                                    <span key={i} className="wipe-auto-result-word">{word}</span>
                                ))}
                            </div>
                            {autoAction.barcode && <div className="wipe-auto-result-barcode">{autoAction.barcode}</div>}
                        </div>
                    ) : (
                        <>
                            <ScanBarcode size={120} strokeWidth={1.2} className="wipe-scan-prompt-icon" />
                            {loading ? (
                                // Waiting on the scan lookup — show a spinner where the
                                // command word goes so the worker knows it's working.
                                <Loader2 className="wipe-spinner wipe-scan-spinner" size={72} strokeWidth={2} />
                            ) : (
                                <div className="scan-command">
                                    {t("wipe_scan_prompt").toUpperCase().split(" ").map((word, i) => (
                                        <FitWord key={i}>{word}</FitWord>
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}

            {phase === "decide" && item && (
                <div className="wipe-item-card">
                    {/* X — abandon this item without recording and go back to scanning */}
                    <button type="button" className="wipe-close-x" onClick={goBack} aria-label={t("close")} title={t("close")}>
                        <X size={22} strokeWidth={2.2} />
                    </button>
                    <div className="wipe-item-desc">{item.itemDesc || item.asin || item.scannedBarcode}</div>
                    <div className="wipe-item-meta">
                        {item.asin && <span>ASIN: {item.asin}</span>}
                        {item.netCost != null && <span className="wipe-item-cost">£{Number(item.netCost).toFixed(2)} net</span>}
                    </div>

                    {/* Data-sensitive choice */}
                    <div className="wipe-choice">
                        <div className="wipe-choice-q">{t("wipe_data_sensitive_q")}</div>
                        <div className="wipe-choice-row">
                            <button
                                className={`wipe-choice-btn ${dataSensitive ? "wipe-choice-btn--active" : ""}`}
                                onClick={() => pickSensitivity(true)}
                            >
                                <HardDrive className="wipe-choice-icon" size={34} strokeWidth={1.6} />
                                <span>{t("wipe_yes")}</span>
                            </button>
                            <button
                                className={`wipe-choice-btn ${!dataSensitive ? "wipe-choice-btn--active" : ""}`}
                                onClick={() => pickSensitivity(false)}
                            >
                                <Ban className="wipe-choice-icon" size={34} strokeWidth={1.6} />
                                <span>{t("wipe_no")}</span>
                            </button>
                        </div>
                        {aiSuggestion != null && (
                            <div className="wipe-choice-ai">
                                {t("wipe_ai_suggests")}: {aiSuggestion ? t("wipe_yes") : t("wipe_no")}
                            </div>
                        )}
                        {humanSuggestion != null && (
                            <div className="wipe-choice-human">
                                {t("wipe_human_suggests")}: {humanSuggestion ? t("wipe_yes") : t("wipe_no")}
                                {humanName ? ` (${humanName})` : ""}
                            </div>
                        )}
                    </div>

                    {/* Outcome */}
                    <div className={`wipe-outcome wipe-outcome--${outcome}`}>
                        <Eraser size={44} strokeWidth={1.8} />
                        <div className="wipe-outcome-title">{t(`wipe_outcome_${outcome}`).split(" ").filter(Boolean).join(" + ")}</div>
                    </div>

                    {/* Wipe instructions — loader while Claude responds, then numbered steps */}
                    {outcome === "data_wipe_relabel" && (
                        <div className="wipe-instructions">
                            <div className="wipe-instructions-title">{t("wipe_instructions_title")}</div>
                            {instructionsLoading ? (
                                <div className="wipe-instructions-loading"><Loader2 className="wipe-spinner" size={30} /></div>
                            ) : (
                                <ol className="wipe-instructions-list">
                                    {parseWipeSteps(instructions).map((step, i) => (
                                        <li key={i}>{step}</li>
                                    ))}
                                </ol>
                            )}
                        </div>
                    )}

                    {isBoxCarton ? (
                        /* Multi-unit box scanned by its carton — print one referenced
                           barcode per unit in one go. No single-unit Confirm here: the
                           worker hasn't picked a unit, so a lone label would be
                           ambiguous. To relabel just one, they scan that unit's LPN.
                           Not data-bearing ⇒ nothing to print, but the box is still
                           RECORDED (one row carrying the unit count, Pink/Yellow) so
                           the worker's answer is saved against the ASIN — previously
                           this button only closed the screen, so a "No" was lost and
                           the box asked again on every scan. */
                        <button
                            className="btn btn-success btn-xl btn-full wipe-confirm-all"
                            onClick={() => confirm({ quantity: boxUnits })}
                            disabled={loading || readOnly}
                            aria-disabled={readOnly}
                            title={readOnly ? t("read_only_mode") : undefined}
                        >
                            {dataSensitive
                                ? t("wipe_print_all").replace("{count}", String(boxUnits))
                                : t("wipe_record")}
                        </button>
                    ) : (
                        <button
                            className="btn btn-success btn-xl btn-full wipe-confirm"
                            onClick={() => confirm()}
                            disabled={loading || readOnly}
                            aria-disabled={readOnly}
                            title={readOnly ? t("read_only_mode") : undefined}
                        >
                            {t("wipe_record")}
                        </button>
                    )}
                </div>
            )}

            {/* Recorded banner — click "Go back" to re-open & correct the item.
                Only shown on the scan screen, never over the decide screen. */}
            {phase === "scan" && lastRecord && (
                <div className="wipe-recorded">
                    <span>{t("wipe_recorded")}</span>
                    {lastRecord.relabelBarcodes.length > 0 && (
                        <span className="wipe-recorded-barcode">{lastRecord.relabelBarcodes[0]}</span>
                    )}
                    <button
                        className="wipe-recorded-undo"
                        onClick={goBackToRecord}
                        disabled={readOnly}
                        aria-disabled={readOnly}
                        title={readOnly ? t("read_only_mode") : undefined}
                    ><RotateCcw size={16} />{t("wipe_go_back")}</button>
                </div>
            )}

            <HiddenScanInput
                value={scanValue}
                onChange={setScanValue}
                onSubmit={doScan}
                disabled={loading || phase === "decide" || !!coverPrompt || !!unmanifestedPrompt}
                placeholder={individual ? t("wipe_scan_lpn_prompt") : t("wipe_scan_prompt")}
                printConnected={printerOnline && !!printMode}
                onPrintClick={() => setShowPrinterSelect(true)}
                reprintMode={reprintMode}
                onReprintModeToggle={() => setReprintMode(v => !v)}
                extraToggle={<div className="wipe-extra-toggles">{modePill}{statsPill}</div>}
                disableCamera
                keyboardOnLeft
            />

            {coverPrompt && (
                /* BLUE: the box stays whole and is sold as a box. */
                <div className="wipe-cover-overlay" role="dialog" aria-modal="true">
                    <div className="wipe-cover-modal wipe-cover-modal--blue">
                        <button type="button" className="wipe-close-x" onClick={() => setCoverPrompt(null)} disabled={loading} aria-label={t("close")} title={t("close")}>
                            <X size={22} strokeWidth={2.2} />
                        </button>
                        <HardDrive size={40} strokeWidth={1.6} className="wipe-cover-icon" />
                        <div className="wipe-cover-pallet">{t("wipe_outcome_box_blue")}</div>
                        <div className="wipe-cover-title">{t("wipe_cover_title")}</div>
                        <div className="wipe-cover-body">
                            {t("wipe_cover_prompt").replace(/\{count\}/g, String(coverPrompt.count))}
                        </div>
                        <div className="wipe-cover-actions">
                            <button
                                className="btn btn-secondary btn-lg"
                                onClick={() => setCoverPrompt(null)}
                                disabled={loading}
                            >
                                {t("wipe_cover_cancel")}
                            </button>
                            <button
                                className="btn btn-success btn-lg"
                                onClick={() => recordCoverLabels(coverPrompt)}
                                disabled={loading || readOnly}
                                aria-disabled={readOnly}
                                title={readOnly ? t("read_only_mode") : undefined}
                            >
                                {t("wipe_cover_confirm").replace("{count}", String(coverPrompt.count))}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {scanLpnsPrompt && (
                /* PINK: the box splits — the scanner stays live under this prompt
                   (see doScan); the first LPN scan closes it and runs as a normal
                   item. The only tap is the small "cover instead" fallback. */
                <div className="wipe-cover-overlay" role="dialog" aria-modal="true">
                    <div className="wipe-cover-modal wipe-cover-modal--pink">
                        <button type="button" className="wipe-close-x" onClick={() => setScanLpnsPrompt(null)} disabled={loading} aria-label={t("close")} title={t("close")}>
                            <X size={22} strokeWidth={2.2} />
                        </button>
                        <ScanBarcode size={44} strokeWidth={1.6} className="wipe-cover-icon" />
                        <div className="wipe-cover-pallet">{t("wipe_outcome_box_pink")}</div>
                        <div className="wipe-cover-title">{t("wipe_cover_title")}</div>
                        <div className="wipe-cover-body">
                            {t("wipe_scan_lpns_prompt").replace(/\{count\}/g, String(scanLpnsPrompt.count))}
                        </div>
                        <button
                            type="button"
                            className="wipe-cover-link"
                            onClick={() => recordCoverLabels({ count: scanLpnsPrompt.count, jliPkgId: scanLpnsPrompt.jliPkgId })}
                            disabled={loading || readOnly}
                            aria-disabled={readOnly}
                            title={readOnly ? t("read_only_mode") : undefined}
                        >
                            {t("wipe_scan_lpns_cover").replace("{count}", String(scanLpnsPrompt.count))}
                        </button>
                    </div>
                </div>
            )}

            {unmanifestedPrompt && (
                <div className="wipe-cover-overlay" role="dialog" aria-modal="true">
                    <div className="wipe-cover-modal wipe-cover-modal--unmanifested">
                        <button type="button" className="wipe-close-x" onClick={() => setUnmanifestedPrompt(null)} disabled={loading} aria-label={t("close")} title={t("close")}>
                            <X size={22} strokeWidth={2.2} />
                        </button>
                        <Ban size={40} strokeWidth={1.6} className="wipe-cover-icon" />
                        <div className="wipe-cover-title">{t("wipe_unmanifested_title")}</div>
                        <div className="wipe-cover-body">
                            {t("wipe_unmanifested_prompt").replace("{lpn}", unmanifestedPrompt.lpn)}
                        </div>
                        <div className="wipe-cover-actions">
                            <button
                                className="btn btn-secondary btn-lg"
                                onClick={() => setUnmanifestedPrompt(null)}
                                disabled={loading}
                            >
                                {t("wipe_cover_cancel")}
                            </button>
                            <button
                                className="btn btn-lg wipe-btn-black"
                                onClick={() => recordUnmanifested(unmanifestedPrompt)}
                                disabled={loading || readOnly}
                                aria-disabled={readOnly}
                                title={readOnly ? t("read_only_mode") : undefined}
                            >
                                {t("wipe_unmanifested_confirm")}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showPrinterSelect && (
                <PrinterSelectModal
                    autoPrint={false}
                    autoPrintEnabled={autoPrint}
                    onToggleAutoPrint={toggleAutoPrint}
                    printMode={printMode}
                    onSelect={() => { setShowPrinterSelect(false); setPrintMode("network"); setPrinterOnline(true); }}
                    onConnectLocal={() => { setShowPrinterSelect(false); setPrintMode("local"); setPrinterOnline(true); }}
                    onDisconnect={() => { setShowPrinterSelect(false); setPrintMode(null); setPrinterOnline(false); }}
                    onClose={() => { setShowPrinterSelect(false); detectPrinter(); }}
                />
            )}
        </div>
    );
};

export default Wipe;
